import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    MenuItem,
    FormControlLabel,
    Switch,
    Stack,
    Alert,
} from '@mui/material';
import { PROMO_KIND_CODE, PROMO_KIND_DATE } from '../../promos/promoTypes';
import { SUPPORTED_DISCOUNT_PERCENTS } from '../../promos/promoSkus';
import { upsertPromo } from '../../promos/promoFirestore';

/**
 * Convert a millis timestamp into the value expected by an
 * <input type="datetime-local">. Browsers want local time, no
 * timezone suffix, with second precision.
 */
function millisToInputValue(ms) {
    if (!ms) return '';
    const d = new Date(ms);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function inputValueToMillis(str) {
    if (!str) return null;
    const t = Date.parse(str);
    return Number.isNaN(t) ? null : t;
}

const emptyPromo = {
    id: null,
    kind: PROMO_KIND_CODE,
    name: '',
    code: '',
    discountPercent: SUPPORTED_DISCOUNT_PERCENTS[0],
    startsAt: null,
    endsAt: null,
    active: true,
};

const PromoForm = ({ initialPromo, onSaved, onCancel }) => {
    const [form, setForm] = useState(emptyPromo);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm(initialPromo ? { ...emptyPromo, ...initialPromo } : emptyPromo);
        setError(null);
    }, [initialPromo]);

    const handleChange = (field) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleDateChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: inputValueToMillis(e.target.value) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!form.name.trim()) {
            setError('Name is required (internal label, not shown to buyers).');
            return;
        }
        if (form.kind === PROMO_KIND_CODE && !form.code.trim()) {
            setError('Code is required for code-type promos.');
            return;
        }
        if (form.kind === PROMO_KIND_DATE && (!form.startsAt || !form.endsAt)) {
            setError('Date-based promos need both a start and end.');
            return;
        }
        if (form.startsAt && form.endsAt && form.endsAt < form.startsAt) {
            setError('End date must be after start date.');
            return;
        }

        setSaving(true);
        try {
            const id = await upsertPromo(form);
            onSaved && onSaved(id);
        } catch (err) {
            setError(err.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {form.id ? 'Edit promo' : 'New promo'}
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Stack spacing={2}>
                <TextField
                    select
                    label="Kind"
                    value={form.kind}
                    onChange={handleChange('kind')}
                >
                    <MenuItem value={PROMO_KIND_CODE}>Code (per-influencer)</MenuItem>
                    <MenuItem value={PROMO_KIND_DATE}>Date window (sitewide)</MenuItem>
                </TextField>

                <TextField
                    label="Name (internal)"
                    value={form.name}
                    onChange={handleChange('name')}
                    placeholder="e.g. Emma collab Q2 2026"
                    required
                />

                {form.kind === PROMO_KIND_CODE && (
                    <TextField
                        label="Code"
                        value={form.code || ''}
                        onChange={handleChange('code')}
                        placeholder="EMMA"
                        helperText="Case-insensitive. Shoppers type it or click a ?promo=EMMA link."
                        required
                    />
                )}

                <TextField
                    select
                    label="Discount %"
                    value={form.discountPercent}
                    onChange={handleChange('discountPercent')}
                    helperText="Add new levels in src/promos/promoSkus.js after cloning the SKUs in Zazzle."
                >
                    {SUPPORTED_DISCOUNT_PERCENTS.map((pct) => (
                        <MenuItem key={pct} value={pct}>{pct}% off</MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="Starts at (optional for codes)"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={millisToInputValue(form.startsAt)}
                    onChange={handleDateChange('startsAt')}
                />
                <TextField
                    label="Ends at (optional for codes)"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={millisToInputValue(form.endsAt)}
                    onChange={handleDateChange('endsAt')}
                />

                <FormControlLabel
                    control={<Switch checked={form.active} onChange={handleChange('active')} />}
                    label="Active (master switch independent of dates)"
                />

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button onClick={onCancel} disabled={saving}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={saving}>
                        {form.id ? 'Save changes' : 'Create promo'}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
};

export default PromoForm;
